import {
  BookmarkSimple,
  Camera,
  ChatCircle,
  Gear,
  Heart,
  PencilSimple,
  Star,
  User,
} from '@phosphor-icons/react';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { events, users as usersApi } from '../../api/endpoints';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import { ScreenError, ScreenLoader } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { BadgeOfficiel, SectionTitle } from '../../components/ui/Bits';
import { PhotoPicker } from '../../components/ui/PhotoPicker';
import { useAsync } from '../../hooks/useAsync';
import { timeAgo } from '../../utils/format';
import { useSession } from '../../store/SessionContext';
import { ServerIllustration } from '../../theme/illustrations';
import { PostText } from '../../components/ui/PostText';
import { EventCard } from '../../components/EventCard';
import { locale, t } from '../../i18n';

/**
 * Onglet Profil.
 *
 * Meme en-tete que les autres onglets ; l'avatar vient
 * s'y poser a cheval sur le bas de l'illustration. Le tout vit dans la
 * zone defilante, pour que l'illustration s'efface au defilement comme
 * ailleurs dans l'application.
 */
export function ProfileScreen() {
  const { server, setUser, ambiance } = useSession();

  /*
    Le profil n'utilise pas `ServerScreen` — son avatar chevauche
    l'illustration, ce qu'aucun autre onglet ne fait — mais il porte le
    meme geste. Sans relecture : il n'etire que pour repondre au doigt.
    Tout ce qui est au-dessus de « Mes destinations » reste en place :
    c'est la carte de visite, elle n'a pas a glisser.
  */
  const zone = useRef<HTMLDivElement | null>(null);
  const { data, loading, error, reload, setData } = useAsync(
    () => usersApi.myProfile(),
    [],
    'profile',
  );

  /*
    Ma note d'organisateur, gardee comme le reste de l'ecran.

    Elle vivait dans un etat local, qui repart de rien a chaque montage :
    quitter l'onglet et y revenir la faisait disparaitre puis reapparaitre
    une fraction de seconde plus tard. Avec une cle de cache, la derniere
    valeur connue s'affiche tout de suite et la relecture se fait derriere.
  */
  const myId = data?.user.id;
  const { data: noteOrga } = useAsync(
    () => (myId ? events.organizerRating(myId) : Promise.resolve(null)),
    [myId],
    'ma-note-orga',
  );
  const corps = useRef<HTMLDivElement | null>(null);
  usePullToRefresh(zone, corps);

  /** La photo part sans confirmation : on la voit changer, cela suffit. */
  async function savePhoto(avatarUrl: string) {
    try {
      const updated = await usersApi.update({ avatarUrl });
      setUser(updated);
      setData((previous) => ({ ...previous, user: updated }));
    } catch {
      reload();
    }
  }

  return (
    <div className="server-screen">
      <div ref={zone} className="scroll-area server-screen__scroll pad-tabbar">
        <div className="pr-hero">
          <header className="server-screen__hero">
            <ServerIllustration
              name={server?.theme.illustration ?? 'marrakech'}
              ambiance={ambiance}
            />
          </header>

          {data && (
            <span className="pr-avatar">
              {/* 240 px de diametre a 3x sur la maquette, hors anneau. */}
              <Avatar user={data.user} size={80} ring />
              {/* La photo se change ici meme ; le reste du profil, aux reglages. */}
              <PhotoPicker
                preset="avatar"
                label={t.profil.commun.changerPhoto}
                className="pr-avatar__edit"
                onPicked={savePhoto}
              >
                <Camera size={12} weight="fill" />
              </PhotoPicker>
            </span>
          )}
        </div>

        <div className="server-screen__body">
          <div className="pr-body">
            {loading && <ScreenLoader />}
            {error && <ScreenError message={error} onRetry={reload} />}

            {data && (
              <>
                <div className="pr-head">
                  {/*
                  Signet et amis empiles, comme l'engrenage et le crayon en
                  face : deux icones de meme taille, l'une sous l'autre.
                */}
                  <div className="pr-head__stack">
                    <Link
                      to="/app/feed/bookmarks"
                      className="pr-head__link"
                      aria-label={t.profil.commun.mesSignets}
                    >
                      <BookmarkSimple size={22} />
                    </Link>
                    <Link to="/app/profile/friends" className="pr-head__link" aria-label={t.profil.commun.mesAmis}>
                      <User size={22} />
                    </Link>
                  </div>
                  {/*
                  Engrenage et crayon empiles, sans pastille ni fond : deux
                  icones de meme taille, l'une sous l'autre. Le crayon mene
                  la ou la bio se modifie vraiment, avec le pseudo et
                  l'avatar.
                */}
                  <div className="pr-head__stack">
                    <Link
                      to="/app/profile/settings"
                      className="pr-head__link"
                      aria-label={t.profil.commun.parametres}
                    >
                      <Gear size={22} />
                    </Link>
                    <Link
                      to="/app/profile/settings/account"
                      className="pr-head__link"
                      aria-label={t.profil.ecran.modifier}
                    >
                      <PencilSimple size={22} />
                    </Link>
                  </div>
                </div>

                {/*
                  Le pseudo tombe **pile sous la photo**, quoi qu'il ait a
                  ses cotes. Une simple ligne centree le decalait des que
                  la note apparaissait, puisqu'elle n'a pas la largeur de
                  ce qui se pose a droite. Trois colonnes reglent cela :
                  les deux bords se partagent la place, le milieu reste au
                  milieu.

                  La colonne de droite ne porte plus que la coche du
                  compte officiel, et reste donc vide pour presque tout le
                  monde. Elle demeure : c'est elle qui tient le pseudo
                  centre quand la note apparait a gauche.
                */}
                <div className="pr-identity">
                  <span className="pr-identity__cote pr-identity__cote--gauche">
                    {noteOrga && (
                      <span
                        className="pr-orga"
                        aria-label={t.profil.commun.notePar(noteOrga.moyenne.toLocaleString(locale), noteOrga.nombre)}
                      >
                        <Star size={14} weight="fill" />
                        {noteOrga.moyenne.toLocaleString(locale)}
                      </span>
                    )}
                  </span>
                  <span className="pr-identity__name">{data.user.username}</span>
                  <span className="pr-identity__cote">
                    {(data.user.officiel || data.user.verifie) && (
                      <BadgeOfficiel size="sm" application={data.user.officiel} />
                    )}
                    {data.user.badge && (
                      <span className="drapeau pr-badge" aria-hidden="true">
                        {data.user.badge}
                      </span>
                    )}
                  </span>
                </div>

                {/* La bio pose sur le fond, sans cadre : la mention suffit a
                  dire ce qu'on lit. Elle se modifie aux reglages, ou le
                  crayon de l'en-tete mene. Sans bio, le mot « Bio » seul
                  annoncait un vide — mieux vaut ne rien mettre. */}
                {data.user.bio.trim() && (
                  <div className="pr-bio">
                    <span className="pr-bio__label">{t.profil.commun.bio}</span>
                    <p className="pr-bio__text">{data.user.bio}</p>
                  </div>
                )}

                <div ref={corps} className="pr-pull">
                  <SectionTitle action={t.profil.commun.voirTout} to="/app/profile/destinations">
                    {t.profil.commun.mesDestinations}
                  </SectionTitle>
                  <div className="card-list">
                    {data.destinations.map((destination) => (
                      <div key={destination.serverId} className="pr-dest-row">
                        <span className="pr-dest-row__name">{destination.serverName}</span>
                      </div>
                    ))}
                  </div>

                  <SectionTitle action={t.profil.commun.voirTout} to="/app/feed/mine">
                    {t.profil.commun.mesPosts}
                  </SectionTitle>
                  {/* Les deux derniers posts partagent une seule carte a filets. */}
                  {data.posts.length > 0 && (
                    <div className="card-list">
                      {data.posts.slice(0, 2).map((post) => (
                        <article key={post.id} className="pr-post">
                          <Avatar user={post.author} size={32} />
                          <div className="pr-post__body">
                            <PostText
                              className="pr-post__content"
                              content={post.content}
                              mentions={post.mentions}
                            />
                            <div className="pr-post__stats">
                              {/* Un zero ne dit rien qu'un signe vide ne
                                  dise deja : on n'ecrit que ce qu'on a. */}
                              <span className="pr-post__stat">
                                <ChatCircle size={15} />
                                {post.commentCount > 0 && post.commentCount}
                              </span>
                              <span className="pr-post__stat">
                                <Heart size={15} />
                                {post.likeCount > 0 && post.likeCount}
                              </span>
                            </div>
                          </div>
                          <span className="pr-post__time">{timeAgo(post.createdAt)}</span>
                        </article>
                      ))}
                    </div>
                  )}

                  <SectionTitle action={t.profil.commun.voirTout} to="/app/events/mine">
                    {t.profil.ecran.mesEvenementsSorties}
                  </SectionTitle>
                  {/*
                Trois evenements dans une seule carte : chaque ligne porte
                sa propre barre de categorie, la carte n'en a plus.
              */}
                  {data.events.length > 0 && (
                    <div className="card-list">
                      {data.events.slice(0, 3).map((event) => (
                        <EventCard key={event.id} event={event} />
                      ))}
                    </div>
                  )}

                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
